'use client'

import { LineClose } from '@qr-front/icons/line-close'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { menuDataStateAnimationCls } from 'helpers'
import * as React from 'react'

import { cn } from '@/utils'

const BottomDialog = DialogPrimitive.Root

const BottomDialogTrigger = DialogPrimitive.Trigger

const BottomDialogPortal = DialogPrimitive.Portal

const BottomDialogClose = DialogPrimitive.Close

const BottomDialogOverlay = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Overlay>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
	<DialogPrimitive.Overlay
		ref={ref}
		className={cn(
			'fixed inset-0 z-50 bg-popup-overlay data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
			className,
		)}
		{...props}
	/>
))
BottomDialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const BottomDialogContent = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => {
	return (
		<React.Fragment>
			<BottomDialogOverlay />
			<DialogPrimitive.Content
				ref={ref}
				className={cn(
					menuDataStateAnimationCls,
					'fixed bottom-0 left-0 top-auto z-50 w-full max-w-full translate-y-safe-area-bottom transform-none rounded-t-10 bg-white p-0 duration-200',
					className,
				)}
				{...props}
			>
				<DialogPrimitive.Close className="absolute right-5 top-[38px] -translate-y-full">
					<LineClose className="size-4 text-dark-grey" />
				</DialogPrimitive.Close>
				{children}
			</DialogPrimitive.Content>
		</React.Fragment>
	)
})
BottomDialogContent.displayName = DialogPrimitive.Content.displayName

const BottomDialogHeader = ({
	className,
	...props
}: React.HTMLAttributes<HTMLDivElement>) => (
	<div className={cn('flex flex-col text-center', className)} {...props} />
)
BottomDialogHeader.displayName = 'BottomDialogHeader'

const BottomDialogFooter = ({
	className,
	...props
}: React.HTMLAttributes<HTMLDivElement>) => (
	<div
		className={cn('flex flex-wrap gap-6 *:grow *:[flex-basis:0]', className)}
		{...props}
	/>
)
BottomDialogFooter.displayName = 'BottomDialogFooter'

const BottomDialogTitle = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Title>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
	<DialogPrimitive.Title
		ref={ref}
		className={cn('type-heading-3', className)}
		{...props}
	/>
))
BottomDialogTitle.displayName = DialogPrimitive.Title.displayName

const BottomDialogDescription = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Description>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
	<DialogPrimitive.Description
		ref={ref}
		className={cn('text-body-medium', className)}
		{...props}
	/>
))
BottomDialogDescription.displayName = DialogPrimitive.Description.displayName

export {
	BottomDialog,
	BottomDialogClose,
	BottomDialogContent,
	BottomDialogDescription,
	BottomDialogFooter,
	BottomDialogHeader,
	BottomDialogOverlay,
	BottomDialogPortal,
	BottomDialogTitle,
	BottomDialogTrigger,
}
